import React, { useState } from 'react';
import {
  Home,
  CalendarDays,
  MessageSquare,
  Users,
  Building2,
  Network,
  Lock,
  Folder,
  ChevronDown,
  ChevronsUpDown,
  Sparkles,
  X,
  HelpCircle,
  Plus,
  Settings,
  Moon,
  Sun,
  ShieldCheck,
  LogOut,
  Trash2
} from 'lucide-react';
import { ViewMode, Meeting } from '../types';
import type { AppUser, MeetingFolderDTO } from '../global';
import { FeedbackModal } from './FeedbackModal';
import { t } from '../lib/i18n';
import { planName } from '../features/preferences/plans';
import { buttonRowProps, draggedMeetingId, isMeetingDrag, meetingDragProps } from '../features/folders/dragMeeting';

/** "Birlikte çalışın" kartı bir kez kapatılınca bir daha gösterilmez. */
const WORK_TOGETHER_DISMISSED_KEY = 'zabit.workTogetherDismissed';

function readWorkTogetherDismissed(): boolean {
  try {
    return window.localStorage.getItem(WORK_TOGETHER_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function saveWorkTogetherDismissed(): void {
  try {
    window.localStorage.setItem(WORK_TOGETHER_DISMISSED_KEY, '1');
  } catch {
    // Depolama kullanılamıyorsa kart yalnızca bu oturum için kapanır.
  }
}

/** Sürüklerken vurgulanan hedef: klasör kimliği ya da "Özel" satırı. */
const PRIVATE_DROP = 'private';

/** mm:ss — kenar çubuğundaki süren kayıt sayacı. */
function formatSidebarDuration(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const sec = Math.floor(totalSeconds % 60);
  return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
}

interface SidebarProps {
  currentView: ViewMode;
  onNavigate: (view: ViewMode) => void;
  meetings: Meeting[];
  selectedMeetingId?: string;
  onSelectMeeting: (meeting: Meeting) => void;
  onNewNote: () => void;
  /** Köprüde klasör var mı; masaüstünde yok, + eski davranışını korur. */
  foldersEnabled: boolean;
  folders: MeetingFolderDTO[];
  selectedFolderId: string | null;
  onCreateFolder: (name: string) => void;
  onSelectFolder: (folderId: string) => void;
  /** `folderId` null ise toplantı klasörden çıkar. */
  onMoveMeeting: (meetingId: string, folderId: string | null) => void;
  isOpen: boolean;
  isDarkMode: boolean;
  onToggleTheme: () => void;
  user: AppUser | null;
  isAdmin: boolean;
  onSignOut: () => void;
  /** Ayarlar'ın Abonelik bölümünü açar. */
  onOpenSubscription: () => void;
  /** Kayıt sürüyor mu — kayıt artık sayfadan çıkınca da devam ediyor. */
  isRecording: boolean;
  recordingSeconds: number;
  recordingTitle: string;
  onOpenRecording: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentView,
  onNavigate,
  meetings,
  selectedMeetingId,
  onSelectMeeting,
  onNewNote,
  foldersEnabled,
  folders,
  selectedFolderId,
  onCreateFolder,
  onSelectFolder,
  onMoveMeeting,
  isOpen,
  isDarkMode,
  onToggleTheme,
  user,
  isAdmin,
  onSignOut,
  onOpenSubscription,
  isRecording,
  recordingSeconds,
  recordingTitle,
  onOpenRecording
}) => {
  const [privateExpanded, setPrivateExpanded] = useState(true);
  const [sharedExpanded, setSharedExpanded] = useState(true);
  const [showWorkTogetherCard, setShowWorkTogetherCard] = useState(() => !readWorkTogetherDismissed());
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [dragTarget, setDragTarget] = useState<string | null>(null);

  if (!isOpen) {
    return null;
  }

  const displayName = user?.name ?? t('Misafir Kullanıcı', 'Guest');
  // Plan hesabın kendi alanından geliyor (profiles.plan).
  const planLabel = t(`${planName(user?.plan ?? 'basic')} planı`, `${planName(user?.plan ?? 'basic')} plan`);
  // Ultra ve Kurumsal'ın üstünde plan yok: düğme aynı sayfaya gider, adı değişir.
  const canUpgrade = !user?.plan || user.plan === 'basic' || user.plan === 'pro';

  // Gezinme öğeleri. Hepsi artık gerçek ekranlara bağlı — daha önce dördü
  // `disabled` olduğu için App.tsx'teki karşılıkları hiç açılamıyordu.
  // Ayarlar yalnızca yöneticide listeye giriyor.
  const navItems: Array<{ id: ViewMode; label: string; icon: typeof Home }> = [
    { id: 'home', label: t('Ana Sayfa', 'Home'), icon: Home },
    { id: 'daily-digest', label: t('Günlük Özet', 'Daily Digest'), icon: CalendarDays },
    { id: 'chat', label: t('Sohbet', 'Chat'), icon: MessageSquare },
    { id: 'people', label: t('Kişiler', 'People'), icon: Users },
    { id: 'companies', label: t('Şirketler', 'Companies'), icon: Building2 },
    { id: 'integrations', label: t('Entegrasyonlar', 'Integrations'), icon: Network },
    { id: 'trash', label: t('Çöp kutusu', 'Trash'), icon: Trash2 },
    ...(isAdmin ? [{ id: 'settings' as ViewMode, label: t('Yönetim', 'Admin'), icon: ShieldCheck }] : []),
  ];

  const privateMeetings = meetings.filter(m => m.space === 'private' && !m.folderId);
  const sharedMeetings = meetings.filter(m => m.space === 'shared' && !m.folderId);

  const finishFolder = () => {
    const name = folderName.trim();
    setFolderName('');
    setIsCreatingFolder(false);
    if (name) onCreateFolder(name);
  };

  const toggleFolder = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (!next.delete(folderId)) next.add(folderId);
      return next;
    });
  };

  /** Klasör satırı ya da (`null`) klasörden çıkaran "Özel" satırı. */
  const dropTarget = (folderId: string | null) => {
    const target = folderId ?? PRIVATE_DROP;
    return {
      onDragOver: (event: React.DragEvent) => {
        if (!isMeetingDrag(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDragTarget(target);
      },
      onDragLeave: () => setDragTarget((current) => (current === target ? null : current)),
      onDrop: (event: React.DragEvent) => {
        event.preventDefault();
        setDragTarget(null);
        const meetingId = draggedMeetingId(event);
        if (!meetingId) return;
        onMoveMeeting(meetingId, folderId);
        if (folderId) setExpandedFolders((prev) => new Set(prev).add(folderId));
      },
    };
  };

  const renderMeeting = (meeting: Meeting, dotClass: string) => (
    <div
      key={meeting.id}
      {...meetingDragProps(meeting.id)}
      {...buttonRowProps(() => onSelectMeeting(meeting))}
      className={`w-full text-left py-1.5 px-2.5 rounded-md text-[13px] truncate flex items-center gap-2 transition-all cursor-pointer ${
        selectedMeetingId === meeting.id
          ? (isDarkMode ? 'bg-[#1F1F23] text-[#46CBE2] font-semibold' : 'bg-[#EDF6F7] text-[#0E7486] font-semibold')
          : (isDarkMode ? 'text-neutral-400 hover:text-neutral-200 hover:bg-[#19191C]' : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-200/60')
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotClass}`}></span>
      <span className="truncate">{meeting.title}</span>
    </div>
  );

  return (
    <aside 
      id="main-sidebar"
      className={`w-72 max-w-[85vw] h-dvh shrink-0 flex flex-col justify-between border-r select-none transition-colors duration-150 relative z-10 ${
        isDarkMode 
          ? 'bg-[#0C0C0E] border-[#1E1E22] text-[#D8D8E0]'
          : 'bg-[#F5F6F8] border-[#E8E8EE] text-[#1E2024]'
      }`}
    >
      {/* Scrollable Navigation Area */}
      <div className="flex-1 overflow-y-auto px-3 py-3.5 space-y-4 no-scrollbar">

        {/* Brand */}
        <div className="px-3 pt-1 pb-1">
          <span className={`text-sm font-bold tracking-[0.18em] ${isDarkMode ? 'text-[#F0EEE6]' : 'text-neutral-900'}`}>
            TALKNOTE
          </span>
        </div>

        {/* Süren kayıt — kayıt sayfasından çıkıldığında mikrofon kapanmıyor,
            bu yüzden kenar çubuğunda hem göstergesi hem geri dönüş yolu var. */}
        {isRecording && (
          <button
            id="nav-active-recording"
            onClick={onOpenRecording}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-colors text-left ${
              currentView === 'recording'
                ? 'border-rose-500/60 bg-rose-500/15'
                : 'border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/15'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold text-rose-300 truncate">
                {recordingTitle || t('Kayıt sürüyor', 'Recording')}
              </span>
              <span className="block text-[11px] text-rose-400/70">{t('Kayıt sürüyor', 'Recording')}</span>
            </span>
            <span className="text-[12px] font-mono tabular-nums text-rose-300 shrink-0">
              {formatSidebarDuration(recordingSeconds)}
            </span>
          </button>
        )}

        {/* Primary Navigation Menu */}
        <nav className="space-y-0.5 text-[14px] font-medium">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = currentView === item.id && (item.id !== 'home' || !selectedMeetingId);
            return (
              <button
                key={item.id}
                id={`nav-${item.id}`}
                data-tour={`tab-${item.id}`}
                onClick={() => onNavigate(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-left ${
                  active
                    ? (isDarkMode ? 'bg-[#1F1F23] text-[#F0EEE6] font-semibold shadow-xs' : 'bg-[#E5E7EB] text-neutral-900 font-semibold')
                    : (isDarkMode ? 'text-neutral-300 hover:bg-[#19191C] hover:text-[#F0EEE6]' : 'text-neutral-700 hover:bg-neutral-200/70 hover:text-neutral-900')
                }`}
              >
                <Icon className="w-5 h-5 text-neutral-400 shrink-0" />
                <span>{item.label}</span>
                {item.id === 'settings' && (
                  <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-[#1DA4BA]/15 text-[#46CBE2] border border-[#3AC8E0]/30 font-semibold">
                    admin
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Toplantılar ve Notlar: klasörler, klasörsüz toplantılar, paylaşılanlar. */}
        <div className="pt-2.5">
          <div className="flex items-center justify-between px-2.5 py-1.5 text-neutral-500 text-[11px] font-bold tracking-[0.09em] uppercase">
            <span>{t('Toplantılar ve Notlar', 'Meetings and Notes')}</span>
            <div className="flex items-center gap-1.5">
              <button
                id="btn-new-folder"
                onClick={foldersEnabled ? () => setIsCreatingFolder(true) : onNewNote}
                title={foldersEnabled ? t('Yeni klasör', 'New folder') : t('Yeni Craft Notu Oluştur', 'New Craft note')}
                aria-label={foldersEnabled ? t('Yeni klasör', 'New folder') : t('Yeni Craft Notu Oluştur', 'New Craft note')}
                className={`p-1 rounded transition-colors ${isDarkMode ? 'hover:text-[#F0EEE6] hover:bg-[#19191C]' : 'hover:text-black hover:bg-neutral-200'}`}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="space-y-1 mt-1 text-[14px]">
            {isCreatingFolder && (
              <form
                className="flex items-center gap-2.5 px-2.5 py-1"
                onSubmit={(event) => {
                  event.preventDefault();
                  finishFolder();
                }}
              >
                <Folder className="w-4 h-4 text-neutral-400 shrink-0" />
                <input
                  autoFocus
                  value={folderName}
                  maxLength={120}
                  onChange={(event) => setFolderName(event.target.value)}
                  onBlur={finishFolder}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      setFolderName('');
                      setIsCreatingFolder(false);
                    }
                  }}
                  placeholder={t('Klasör adı', 'Folder name')}
                  aria-label={t('Klasör adı', 'Folder name')}
                  className={`w-full min-w-0 rounded-md border px-2 py-1 text-[13px] outline-none ${
                    isDarkMode ? 'bg-[#19191C] border-[#3F3F45] text-neutral-200 focus:border-[#3AC8E0]' : 'bg-white border-neutral-300 text-neutral-900 focus:border-[#1DA4BA]'
                  }`}
                />
              </form>
            )}

            {folders.map((folder) => {
              const inFolder = meetings.filter((meeting) => meeting.folderId === folder.id);
              const expanded = expandedFolders.has(folder.id);
              return (
                <div key={folder.id}>
                  <div
                    {...dropTarget(folder.id)}
                    className={`flex items-center gap-1 rounded-md transition-colors ${
                      dragTarget === folder.id
                        ? 'bg-[#1DA4BA]/20 ring-1 ring-[#3AC8E0]'
                        : selectedFolderId === folder.id
                          ? isDarkMode ? 'bg-[#1F1F23]' : 'bg-[#EDF6F7]'
                          : 'hover:bg-neutral-800/30'
                    }`}
                  >
                    <button
                      onClick={() => toggleFolder(folder.id)}
                      aria-label={expanded ? t('Klasörü daralt', 'Collapse folder') : t('Klasörü genişlet', 'Expand folder')}
                      aria-expanded={expanded}
                      className="text-neutral-400 hover:text-[#F0EEE6] p-1 ml-1 shrink-0"
                    >
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? '' : '-rotate-90'}`} />
                    </button>
                    <button
                      onClick={() => {
                        setExpandedFolders((prev) => new Set(prev).add(folder.id));
                        onSelectFolder(folder.id);
                      }}
                      className="flex items-center gap-2 min-w-0 flex-1 py-1.5 pr-2.5 text-neutral-300 hover:text-[#F0EEE6] text-[13.5px] font-medium text-left"
                    >
                      <Folder className="w-4 h-4 text-neutral-400 shrink-0" />
                      <span className="truncate">{folder.name}</span>
                      <span className="ml-auto text-[11.5px] font-normal text-neutral-500 shrink-0">{inFolder.length}</span>
                    </button>
                  </div>
                  {expanded && (
                    <div className="ml-4 mt-0.5 space-y-0.5 border-l border-neutral-700/30 pl-2.5">
                      {inFolder.length === 0 && (
                        <div className="px-2.5 py-1.5 text-[12px] text-neutral-500 italic">{t('Toplantı sürükleyip bırak', 'Drag a meeting here')}</div>
                      )}
                      {inFolder.map((meeting) => renderMeeting(meeting, 'bg-neutral-500'))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Private Section — klasörsüz toplantılar; üzerine bırakmak klasörden çıkarır. */}
            <div>
              <div
                {...dropTarget(null)}
                className={`flex items-center justify-between group px-2.5 py-1.5 rounded-md transition-colors ${
                  dragTarget === PRIVATE_DROP ? 'bg-[#1DA4BA]/20 ring-1 ring-[#3AC8E0]' : 'hover:bg-neutral-800/30'
                }`}
              >
                <button
                  onClick={() => setPrivateExpanded(!privateExpanded)}
                  className="flex items-center gap-2.5 text-neutral-300 hover:text-[#F0EEE6] text-[13.5px] font-medium"
                >
                  <Lock className="w-4 h-4 text-neutral-400" />
                  <span>{t('Özel', 'Private')}</span>
                </button>
                <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={onNewNote}
                    title={t('Bu alana not ekle', 'Add a note to this space')}
                    className="text-neutral-400 hover:text-[#F0EEE6] p-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setPrivateExpanded(!privateExpanded)}
                    className="text-neutral-400 hover:text-[#F0EEE6] p-1"
                  >
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${privateExpanded ? '' : '-rotate-90'}`} />
                  </button>
                </div>
              </div>

              {/* Private Meeting Notes list */}
              {privateExpanded && (
                <div className="ml-4 mt-0.5 space-y-0.5 border-l border-neutral-700/30 pl-2.5">
                  {privateMeetings.length === 0 && (
                    <div className="px-2.5 py-1.5 text-[12px] text-neutral-500 italic">{t('Henüz not yok', 'No notes yet')}</div>
                  )}
                  {privateMeetings.map((meeting) => renderMeeting(meeting, 'bg-neutral-500'))}
                </div>
              )}
            </div>

            {/* Shared with me Section */}
            <div>
              <div className="flex items-center justify-between group px-2.5 py-1.5 rounded-md hover:bg-neutral-800/30 transition-colors">
                <button
                  onClick={() => setSharedExpanded(!sharedExpanded)}
                  className="flex items-center gap-2.5 text-neutral-300 hover:text-[#F0EEE6] text-[13.5px] font-medium"
                >
                  <Users className="w-4 h-4 text-neutral-400" />
                  <span>{t('Paylaşılanlar', 'Shared with me')}</span>
                </button>
                <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => setSharedExpanded(!sharedExpanded)}
                    className="text-neutral-400 hover:text-[#F0EEE6] p-1"
                  >
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${sharedExpanded ? '' : '-rotate-90'}`} />
                  </button>
                </div>
              </div>

              {sharedExpanded && (
                <div className="ml-4 mt-0.5 space-y-0.5 border-l border-neutral-700/30 pl-2.5">
                  {sharedMeetings.length > 0 ? (
                    sharedMeetings.map((meeting) => renderMeeting(meeting, 'bg-[#3AC8E0]'))
                  ) : (
                    <div className="px-2.5 py-1.5 text-[12px] text-neutral-500 italic">{t('Paylaşılan not yok', 'No shared notes')}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Fixed Area (Work Together Card + Profile Footer) */}
      <div className="p-3 space-y-2 border-t border-inherit shrink-0">
        {/* "Work together" Card (Exact replica of bottom banner in screenshot) */}
        {showWorkTogetherCard && (
          <div className={`p-3.5 rounded-lg relative transition-all ${
            isDarkMode
              ? 'bg-[#19191C] text-neutral-200'
              : 'bg-neutral-100 text-neutral-800'
          }`}>
            <button
              onClick={() => {
                setShowWorkTogetherCard(false);
                saveWorkTogetherDismissed();
              }}
              className="absolute top-2 right-2 text-neutral-400 hover:text-neutral-200 p-1 rounded transition-colors"
              title={t('Kapat', 'Close')}
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-lg bg-[#1DA4BA]/20 text-[#46CBE2] flex items-center justify-center shrink-0 border border-[#3AC8E0]/30 mt-0.5">
                <Users className="w-4 h-4" />
              </div>
              <div className="min-w-0 pr-2.5">
                <h4 className="text-[13.5px] font-semibold leading-snug">{t('Birlikte çalışın', 'Work together')}</h4>
                <p className="text-[12px] text-neutral-400 leading-snug mt-0.5">
                  {t('Toplantı ve notları ekibinizle paylaşın.', 'Share meetings and notes with your team.')}
                </p>
                <button
                  disabled
                  title={t('Yakında', 'Coming soon')}
                  className="text-[12.5px] font-semibold text-neutral-500 flex items-center gap-1.5 mt-1.5 cursor-not-allowed"
                >
                  <span>{t('Ekip oluştur', 'Create a team')}</span>
                  <span className="soon-badge">{t('Yakında', 'Soon')}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Feedback — öneri/geri bildirim modalı açar */}
        <button
          onClick={() => setIsFeedbackOpen(true)}
          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13.5px] font-medium text-left transition-colors ${
            isDarkMode ? 'text-neutral-300 hover:bg-[#19191C] hover:text-[#F0EEE6]' : 'text-neutral-700 hover:bg-neutral-200/70 hover:text-neutral-900'
          }`}
        >
          <HelpCircle className="w-4 h-4 text-neutral-400" />
          <span>{t('Geri Bildirim', 'Feedback')}</span>
        </button>

        {/* User Profile Card (Matches Kadir Çet · Basic Plan) */}
        <div className="relative">
          <button
            id="btn-user-profile"
            data-tour="profile"
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className={`w-full flex items-center justify-between p-2.5 rounded-lg transition-all ${
              isDarkMode
                ? 'bg-[#19191C] hover:bg-[#1F1F23] text-neutral-200'
                : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-900'
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              {/* Google hesabının gerçek profil fotoğrafı; yoksa baş harf. */}
              {user?.avatarDataUrl ? (
                <img
                  src={user.avatarDataUrl}
                  alt=""
                  className="w-7 h-7 rounded-full object-cover shadow-xs shrink-0"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-[#1DA4BA] to-indigo-500 text-[#F0EEE6] font-bold text-[13px] flex items-center justify-center shadow-xs shrink-0">
                  {displayName.charAt(0).toLocaleUpperCase('tr')}
                </div>
              )}
              <div className="min-w-0 text-left">
                <div className="text-[13.5px] font-semibold truncate">{displayName}</div>
                <div className="text-[11px] text-neutral-400 flex items-center gap-1.5 truncate">
                  <span>{isAdmin ? t('Yönetici', 'Admin') : planLabel}</span>
                  <span className="w-1 h-1 rounded-full bg-emerald-500"></span>
                </div>
              </div>
            </div>
            <ChevronsUpDown className="w-4 h-4 text-neutral-400 shrink-0" />
          </button>

          {/* Profile Dropdown Menu */}
          {showProfileMenu && (
            <div className={`absolute bottom-full left-0 right-0 mb-2 p-2 rounded-xl border shadow-xl z-50 text-[13.5px] transition-all ${
              isDarkMode ? 'bg-[#19191C] border-[#47474C] text-neutral-200' : 'bg-white border-neutral-200 text-neutral-800'
            }`}>
              <div className="px-3 py-2 border-b border-neutral-700/30 mb-1">
                <div className="font-semibold text-[13px] text-[#F0EEE6] truncate">
                  {user?.email ?? '—'}
                </div>
                <div className={`text-[13px] font-semibold mt-0.5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                  {isAdmin ? t('Yönetici hesabı', 'Admin account') : planLabel}
                </div>
              </div>

              <button
                id="btn-upgrade-plan"
                onClick={() => {
                  onOpenSubscription();
                  setShowProfileMenu(false);
                }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-neutral-700/30 flex items-center gap-2.5 font-medium"
              >
                <Sparkles className="w-4 h-4 text-[#46CBE2]" />
                <span>{canUpgrade ? t('Planı Yükselt', 'Upgrade plan') : t('Aboneliğim', 'My subscription')}</span>
              </button>

              <button
                onClick={() => {
                  onToggleTheme();
                  setShowProfileMenu(false);
                }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-neutral-700/30 flex items-center gap-2.5"
              >
                {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                <span>{isDarkMode ? t('Açık Temaya Geç', 'Switch to light theme') : t('Karanlık Temaya Geç', 'Switch to dark theme')}</span>
              </button>

              {/* Kullanıcı ayarları — herkese açık, tam sayfa ayarlar ekranını açar. */}
              <button
                id="btn-open-settings"
                onClick={() => {
                  onNavigate('preferences');
                  setShowProfileMenu(false);
                }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-neutral-700/30 flex items-center gap-2.5"
              >
                <Settings className="w-4 h-4" />
                <span>{t('Ayarlar', 'Settings')}</span>
              </button>

              {/* Yönetim ekranı yalnızca yöneticide — normal kullanıcıda menüde hiç yok. */}
              {isAdmin && (
                <button
                  onClick={() => {
                    onNavigate('settings');
                    setShowProfileMenu(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-neutral-700/30 flex items-center gap-2.5"
                >
                  <ShieldCheck className="w-4 h-4" />
                  <span>{t('Yönetim', 'Admin')}</span>
                </button>
              )}

              <button
                onClick={() => {
                  setShowProfileMenu(false);
                  onSignOut();
                }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-rose-500/10 hover:text-rose-400 flex items-center gap-2.5 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>{t('Çıkış Yap', 'Sign out')}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <FeedbackModal
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
        isDarkMode={isDarkMode}
        userEmail={user?.email}
      />
    </aside>
  );
};
